/**
 * Accounts — the tenant's account list.
 *
 * Everything on this page comes from `GET /api/v1/accounts/list` under the
 * application the operator selected; there is no seeded, sample, or fallback
 * data in any state. An empty table means the tenant has no matching account,
 * and a failed request is rendered as the server's own error, never as "no
 * accounts".
 *
 * The list is per-tenant by design (no "all applications" option), the selection
 * is never made automatically — not even for a single-application operator — and
 * it is stored under `accounts.application_id`, distinct from the Payments
 * page's own selection.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiRequestError } from '@/lib/api'
import { formatAmount } from '@/pages/accounts/amounts'
import {
  readStoredApplicationId,
  resolveListView,
  resolveRestoredSelection,
  writeStoredApplicationId,
  type ApplicationOption,
} from '@/pages/accounts/applicationContext'
import { fetchAccountList, serverErrorMessage, type AccountRow } from '@/pages/accounts/accountsApi'
import { listFilterParams } from '@/pages/accounts/ledgerViews'
import { shouldApplyResponse, tagRequest } from '@/pages/accounts/requestContext'

/** Enough to list every application an operator owns today; the selector is not paginated. */
const APPLICATIONS_PAGE_SIZE = 100
const PER_PAGE = 20

function bucketSummary(row: AccountRow): string[] {
  return row.sub_accounts.map(
    (bucket) => `${bucket.type}: ${formatAmount(bucket.balance, bucket)}`
  )
}

export default function AccountsPage() {
  const [applications, setApplications] = useState<ApplicationOption[]>([])
  const [applicationsLoaded, setApplicationsLoaded] = useState(false)
  const [applicationId, setApplicationId] = useState<number | null>(null)
  const [rows, setRows] = useState<AccountRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [externalRef, setExternalRef] = useState('')
  const [userId, setUserId] = useState('')
  const [hasBalance, setHasBalance] = useState('')
  const [appliedFilters, setAppliedFilters] = useState({
    externalRef: '', userId: '', hasBalance: '',
  })
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const storage = typeof window === 'undefined' ? null : window.localStorage
  const generation = useRef(0)

  const filters = useMemo(() => listFilterParams(appliedFilters), [appliedFilters])

  const selectApplication = useCallback(
    (id: number | null) => {
      // A new tenant is a new context: rows, pagination and filters of the
      // previous one are dropped rather than carried across (design D14).
      setApplicationId(id)
      setRows([])
      setTotal(0)
      setPage(1)
      setExternalRef('')
      setUserId('')
      setHasBalance('')
      setAppliedFilters({ externalRef: '', userId: '', hasBalance: '' })
      setError(null)
      setLoaded(false)
      generation.current += 1
      writeStoredApplicationId(storage, id)
    },
    [storage]
  )

  useEffect(() => {
    api
      .get<ApplicationOption[]>('/applications', { per_page: APPLICATIONS_PAGE_SIZE })
      .then((res) => {
        const apps = Array.isArray(res.data) ? res.data : []
        setApplications(apps)
        const restored = resolveRestoredSelection(readStoredApplicationId(storage), apps)
        setApplicationId(restored.selected)
        if (restored.cleared) writeStoredApplicationId(storage, null)
      })
      .catch((err: unknown) => {
        setError(
          err instanceof ApiRequestError ? err.error.message : 'Failed to load applications'
        )
      })
      .finally(() => setApplicationsLoaded(true))
  }, [storage])

  const load = useCallback(async () => {
    if (applicationId == null) return
    const tag = tagRequest(
      { applicationId, accountId: null, view: null, filters, page },
      generation.current
    )
    generation.current = tag.generation
    setLoading(true)
    setError(null)
    try {
      const envelope = await fetchAccountList(applicationId, {
        ...filters,
        page,
        per_page: PER_PAGE,
      })
      if (
        !shouldApplyResponse(
          tag,
          { applicationId, accountId: null, view: null, filters, page },
          generation.current
        )
      ) {
        return
      }
      setRows(envelope.accounts)
      setTotal(envelope.total)
      setLoaded(true)
    } catch (err: unknown) {
      if (tag.generation !== generation.current) return
      setError(serverErrorMessage(err, 'Failed to load accounts'))
      setRows([])
      setLoaded(false)
      // A tenant the server rejects must not be replayed silently on reload.
      writeStoredApplicationId(storage, null)
    } finally {
      if (tag.generation === generation.current) setLoading(false)
    }
  }, [applicationId, filters, page, storage])

  useEffect(() => {
    load()
  }, [load])

  const view = resolveListView({
    loading: loading || !applicationsLoaded,
    error,
    selectedApplicationId: applicationId,
    rowCount: rows.length,
    loaded,
  })

  const pages = Math.max(1, Math.ceil(total / PER_PAGE))

  return (
    <main className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Accounts</h1>
        <p className="text-sm text-muted-foreground">
          每个终端用户的账户、各子账户余额与最近流水时间。按应用隔离读取。
        </p>
      </header>

      {error && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <section aria-labelledby="accounts-filters-heading" className="rounded-lg border p-5">
        <h2 id="accounts-filters-heading" className="mb-4 text-lg font-medium">Filters</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Application</span>
            <select
              className="w-full rounded-md border px-3 py-2"
              value={applicationId ?? ''}
              onChange={(e) => selectApplication(e.target.value ? Number(e.target.value) : null)}
              aria-label="Application"
            >
              <option value="">Select an application</option>
              {applications.map((a) => (
                <option key={a.id} value={a.id}>{a.name} (#{a.id})</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">External reference</span>
            <input
              className="w-full rounded-md border px-3 py-2"
              value={externalRef}
              onChange={(e) => setExternalRef(e.target.value)}
              placeholder="exact match"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">User id</span>
            <input
              className="w-full rounded-md border px-3 py-2"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="exact match"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted-foreground">Balance</span>
            <select
              className="w-full rounded-md border px-3 py-2"
              value={hasBalance}
              onChange={(e) => setHasBalance(e.target.value)}
            >
              <option value="">Any</option>
              <option value="true">Has available balance</option>
              <option value="false">No available balance</option>
            </select>
          </label>
        </div>
        <button
          className="mt-4 rounded-md border px-3 py-1 text-sm transition-colors hover:bg-slate-50"
          onClick={() => {
            setPage(1)
            setAppliedFilters({ externalRef, userId, hasBalance })
          }}
        >
          Apply filters
        </button>
      </section>

      <section aria-labelledby="accounts-table-heading" className="rounded-lg border p-5">
        <h2 id="accounts-table-heading" className="mb-4 text-lg font-medium">
          All Accounts{loaded ? ` (${total})` : ''}
        </h2>

        {view === 'error' ? (
          <p className="text-sm text-red-700">Could not load accounts — see the error above.</p>
        ) : view === 'select-application' ? (
          <p className="text-sm text-muted-foreground">
            Select an application to list its accounts. Accounts are scoped per application.
          </p>
        ) : view === 'loading' ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : view === 'empty' ? (
          <p className="text-sm text-muted-foreground">No accounts match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">User</th>
                  <th className="py-2 pr-4 font-medium">Balances</th>
                  <th className="py-2 pr-4 font-medium">Last activity</th>
                  <th className="py-2 pr-4 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.account_id} className="border-b last:border-0 transition-colors hover:bg-slate-50">
                    <td className="py-2 pr-4">
                      <Link className="font-medium underline" to={`/accounts/${row.account_id}`}>
                        {row.external_ref ?? `user #${row.user_id}`}
                      </Link>
                      <div className="font-mono text-xs text-muted-foreground">{row.account_id}</div>
                    </td>
                    <td className="py-2 pr-4">
                      {row.sub_accounts.length === 0 ? (
                        <span className="text-muted-foreground">尚未开通任何子账户</span>
                      ) : (
                        <ul className="space-y-0.5">
                          {bucketSummary(row).map((line) => (
                            <li key={line} className="font-mono text-xs">{line}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {row.last_entry_at ? new Date(row.last_entry_at).toLocaleString() : '—'}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {new Date(row.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {loaded && pages > 1 && (
          <div className="mt-4 flex items-center gap-3 text-sm">
            <button
              className="rounded-md border px-3 py-1 transition-colors hover:bg-slate-50 disabled:opacity-50"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <span className="text-muted-foreground">Page {page} of {pages}</span>
            <button
              className="rounded-md border px-3 py-1 transition-colors hover:bg-slate-50 disabled:opacity-50"
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </main>
  )
}
